import { signOut, useSession } from "@d12labs/auth/client";
import { apiFetch } from "@d12labs/client";
import type { PluginManifest } from "@d12labs/plugins/types";
import { type CommandItem, CommandPalette } from "@d12labs/ui/command-palette";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@d12labs/ui/dropdown-menu";
import {
	BrandSquare,
	navIcon,
	SidebarBrand,
	SidebarNavItem,
	SidebarSection,
} from "@d12labs/ui/sidebar-kit";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import {
	// @scaffold:ai-usage start
	BarChart3,
	// @scaffold:ai-usage end
	Check,
	ChevronsUpDown,
	// @scaffold:account start
	CircleUser,
	// @scaffold:account end
	Home,
	LayoutGrid,
	// @scaffold:jobs start
	ListChecks,
	// @scaffold:jobs end
	Menu,
	Monitor,
	Moon,
	// @scaffold:settings-ui start
	SlidersHorizontal,
	// @scaffold:settings-ui end
	Sun,
	Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type ThemeMode, useTheme } from "@/lib/theme-context";
import { useAvatarUrl } from "@/lib/use-avatar-url";
import { pluginManifests, pluginSidebars } from "../../../plugins/registry";

/** A plugin's primary route prefix (its first nav item). */
const primaryHref = (m: PluginManifest): string | undefined => m.navItems?.[0]?.href;

/** The plugin owning the current path — longest matching nav prefix wins. */
function findActivePlugin(pathname: string): PluginManifest | undefined {
	let best: PluginManifest | undefined;
	let bestLen = -1;
	for (const m of pluginManifests) {
		const href = primaryHref(m);
		if (!href) continue;
		if ((pathname === href || pathname.startsWith(`${href}/`)) && href.length > bestLen) {
			best = m;
			bestLen = href.length;
		}
	}
	return best;
}

const tagline = (s: string) => (s.length > 42 ? `${s.slice(0, 42).trimEnd()}…` : s);

const iconButton =
	"flex size-7 flex-none items-center justify-center rounded-md text-ink-subtle outline-none transition-colors hover:bg-surface-1 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50";

function userInitials(name: string | null | undefined, email: string): string {
	const source = (name ?? "").trim();
	if (source.length > 0) {
		const parts = source.split(/\s+/).filter(Boolean);
		const first = parts[0]?.charAt(0);
		const second = parts[1]?.charAt(0);
		if (first && second) return (first + second).toUpperCase();
		return source.slice(0, 2).toUpperCase();
	}
	return email.slice(0, 2).toUpperCase();
}

/** The app launcher / "Home" sidebar: Home + Apps + System (core) sections. */
function LauncherView({ onNavigate }: { onNavigate?: () => void }) {
	// Admin surfaces only appear for ADMIN_EMAILS users (server still enforces it).
	const { data: access } = useQuery({
		queryKey: ["admin", "access"],
		queryFn: () => apiFetch<{ isAdmin: boolean }>("/api/admin/access"),
		staleTime: 5 * 60 * 1000,
	});
	const isAdmin = access?.isAdmin ?? false;

	return (
		<div className="scrollbar-gutter-stable scrollbar-thin scrollbar-thumb-hairline-strong scrollbar-track-transparent flex min-h-0 flex-1 flex-col overflow-y-auto">
			<SidebarBrand
				icon={<BrandSquare icon={LayoutGrid} />}
				title="d12-starter"
				subtitle="workspace"
			/>
			<div className="flex flex-col gap-1 px-2 py-2">
				<SidebarNavItem to="/home" exact icon={Home} label="Home" onNavigate={onNavigate} />
				<SidebarSection label="Apps">
					{pluginManifests.map((m) => {
						const href = primaryHref(m);
						if (!href) return null;
						return (
							<SidebarNavItem
								key={m.name}
								to={href}
								icon={navIcon(m.navItems?.[0]?.icon)}
								label={m.label}
								onNavigate={onNavigate}
							/>
						);
					})}
				</SidebarSection>
				<SidebarSection label="System">
					{/* Users is always present (Auth is always installed); AI usage is
					    admin-only and the server still enforces ADMIN_EMAILS. */}
					<SidebarNavItem to="/users" icon={Users} label="Users" onNavigate={onNavigate} />
					{isAdmin ? (
						<>
							{/* @scaffold:ai-usage start */}
							<SidebarNavItem
								to="/admin/ai-usage"
								icon={BarChart3}
								label="AI usage"
								onNavigate={onNavigate}
							/>
							{/* @scaffold:ai-usage end */}
							{/* @scaffold:jobs start */}
							<SidebarNavItem
								to="/admin/jobs"
								icon={ListChecks}
								label="Jobs"
								onNavigate={onNavigate}
							/>
							{/* @scaffold:jobs end */}
							{/* @scaffold:settings-ui start */}
							<SidebarNavItem
								to="/admin/settings"
								icon={SlidersHorizontal}
								label="Settings"
								onNavigate={onNavigate}
							/>
							{/* @scaffold:settings-ui end */}
						</>
					) : null}
					{/* @scaffold:account start */}
					<SidebarNavItem to="/account" icon={CircleUser} label="Account" onNavigate={onNavigate} />
					{/* @scaffold:account end */}
				</SidebarSection>
			</div>
		</div>
	);
}

/** Fallback sidebar for plugins that don't ship a custom one. */
function DefaultPluginSidebar({
	plugin,
	onBack,
	onNavigate,
}: {
	plugin: PluginManifest;
	onBack: () => void;
	onNavigate?: () => void;
}) {
	return (
		<div className="scrollbar-gutter-stable scrollbar-thin scrollbar-thumb-hairline-strong scrollbar-track-transparent flex min-h-0 flex-1 flex-col overflow-y-auto">
			<SidebarBrand
				icon={<BrandSquare icon={navIcon(plugin.navItems?.[0]?.icon)} />}
				title={plugin.label}
				subtitle={tagline(plugin.description)}
				onBack={onBack}
			/>
			<div className="px-2 py-2">
				<SidebarSection label="Workspace">
					{(plugin.navItems ?? []).map((item) => (
						<SidebarNavItem
							key={item.href}
							to={item.href}
							icon={navIcon(item.icon)}
							label={item.label}
							onNavigate={onNavigate}
						/>
					))}
				</SidebarSection>
			</div>
		</div>
	);
}

function PluginSidebarView({
	plugin,
	onBack,
	onNavigate,
}: {
	plugin: PluginManifest;
	onBack: () => void;
	onNavigate?: () => void;
}) {
	const Custom = pluginSidebars[plugin.name];
	if (Custom) return <Custom onBack={onBack} onNavigate={onNavigate} />;
	return <DefaultPluginSidebar plugin={plugin} onBack={onBack} onNavigate={onNavigate} />;
}

const THEME_OPTIONS: { mode: ThemeMode; label: string; icon: typeof Sun }[] = [
	{ mode: "system", label: "System", icon: Monitor },
	{ mode: "light", label: "Light", icon: Sun },
	{ mode: "dark", label: "Dark", icon: Moon },
];

function AccountChip({ onNavigate }: { onNavigate?: () => void }) {
	const { data } = useSession();
	const navigate = useNavigate();
	// @scaffold:account start
	const router = useRouter();
	// @scaffold:account end
	const { url: avatarUrl } = useAvatarUrl();
	const { mode, setMode } = useTheme();
	const user = data?.user;
	if (!user) return null;

	const handleSignOut = async () => {
		await signOut();
		await navigate({ to: "/login" });
	};
	// @scaffold:account start
	const handleAccount = () => {
		onNavigate?.();
		router.navigate({ to: "/account" });
	};
	// @scaffold:account end

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				aria-label="Account menu"
				className="flex w-full items-center gap-2.5 rounded-md p-1.5 text-left outline-none transition-colors hover:bg-surface-1 focus-visible:ring-2 focus-visible:ring-ring/50"
			>
				<span className="flex size-8 flex-none items-center justify-center overflow-hidden rounded-full border border-hairline bg-surface-2 text-xs font-medium text-ink">
					{avatarUrl ? (
						<img src={avatarUrl} alt="" className="size-full object-cover" />
					) : (
						userInitials(user.name, user.email)
					)}
				</span>
				<span className="min-w-0 flex-1 truncate text-body-sm text-ink-muted">{user.email}</span>
				<ChevronsUpDown className="h-3.5 w-3.5 flex-none text-ink-subtle" />
			</DropdownMenuTrigger>
			<DropdownMenuContent side="top" align="start" className="min-w-[208px]">
				<DropdownMenuLabel className="text-muted-foreground">Signed in as</DropdownMenuLabel>
				<DropdownMenuLabel className="max-w-[16rem] truncate pt-0 text-sm font-normal text-foreground">
					{user.email}
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{/* @scaffold:account start */}
				<DropdownMenuItem onSelect={handleAccount}>Account</DropdownMenuItem>
				<DropdownMenuSeparator />
				{/* @scaffold:account end */}
				<DropdownMenuLabel className="text-muted-foreground">Theme</DropdownMenuLabel>
				{THEME_OPTIONS.map(({ mode: m, label, icon: Icon }) => (
					<DropdownMenuItem
						key={m}
						onSelect={(e) => {
							// Keep the menu open feel snappy without navigating away.
							e.preventDefault();
							setMode(m);
						}}
					>
						<Icon className="size-4 text-ink-subtle" />
						<span className="flex-1">{label}</span>
						{mode === m ? <Check className="size-4 text-primary" /> : null}
					</DropdownMenuItem>
				))}
				<DropdownMenuSeparator />
				<DropdownMenuItem destructive onSelect={handleSignOut}>
					Sign out
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

export function AppShell() {
	const navigate = useNavigate();
	const router = useRouter();
	const { setMode } = useTheme();
	const [mobileOpen, setMobileOpen] = useState(false);
	const [cmdkOpen, setCmdkOpen] = useState(false);

	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const activePlugin = findActivePlugin(pathname);

	// Escape closes the mobile drawer.
	useEffect(() => {
		if (!mobileOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setMobileOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [mobileOpen]);

	// Global ⌘K / Ctrl+K opens the command palette (toggles).
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setCmdkOpen((o) => !o);
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	// Command palette items: navigation to every installed app + core routes,
	// plus theme actions. Plugins could extend this via a registry later.
	const commandItems = useMemo<CommandItem[]>(() => {
		const go = (to: string) => () => {
			router.navigate({ to });
		};
		const nav: CommandItem[] = [
			{ id: "nav-home", label: "Home", group: "Navigate", icon: Home, onSelect: go("/home") },
		];
		for (const m of pluginManifests) {
			for (const item of m.navItems ?? []) {
				nav.push({
					id: `nav-${m.name}-${item.href}`,
					label: m.navItems && m.navItems.length > 1 ? `${m.label} · ${item.label}` : m.label,
					group: "Navigate",
					keywords: [m.name, m.description],
					icon: navIcon(item.icon),
					hint: item.href,
					onSelect: go(item.href),
				});
			}
		}
		nav.push({
			id: "nav-users",
			label: "Users",
			group: "Navigate",
			icon: Users,
			onSelect: go("/users"),
		});
		// @scaffold:account start
		nav.push({
			id: "nav-account",
			label: "Account",
			group: "Navigate",
			icon: CircleUser,
			onSelect: go("/account"),
		});
		// @scaffold:account end
		const theme: CommandItem[] = THEME_OPTIONS.map(({ mode: m, label, icon }) => ({
			id: `theme-${m}`,
			label: `Theme: ${label}`,
			group: "Preferences",
			icon,
			onSelect: () => setMode(m),
		}));
		return [...nav, ...theme];
	}, [router, setMode]);

	// The sidebar is a pure function of the route: in a plugin → that plugin's
	// sidebar; anywhere else (Home, Account, …) → the launcher. "Back" navigates
	// Home, so the sidebar and the content always agree.
	const sidebarBody = (onNavigate?: () => void) => (
		<>
			{activePlugin ? (
				<PluginSidebarView
					plugin={activePlugin}
					onBack={() => {
						onNavigate?.();
						navigate({ to: "/home" });
					}}
					onNavigate={onNavigate}
				/>
			) : (
				<LauncherView onNavigate={onNavigate} />
			)}
			<div className="flex-none border-t border-hairline p-2">
				<AccountChip onNavigate={onNavigate} />
			</div>
		</>
	);

	return (
		<div className="flex h-dvh overflow-hidden">
			{/* Desktop sidebar */}
			<aside className="hidden h-full w-[244px] shrink-0 flex-col border-r border-hairline bg-background md:flex">
				{sidebarBody()}
			</aside>

			{/* Mobile off-canvas drawer */}
			{mobileOpen ? (
				<div className="fixed inset-0 z-50 md:hidden">
					<button
						type="button"
						aria-label="Close menu"
						className="absolute inset-0 bg-semantic-overlay/60"
						onClick={() => setMobileOpen(false)}
					/>
					<aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-hairline bg-background">
						{sidebarBody(() => setMobileOpen(false))}
					</aside>
				</div>
			) : null}

			{/* Main column */}
			<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
				<div className="flex flex-none items-center gap-3 border-b border-hairline px-4 py-3 md:hidden">
					<button
						type="button"
						onClick={() => setMobileOpen(true)}
						aria-label="Open menu"
						className={iconButton}
					>
						<Menu className="h-4 w-4" />
					</button>
					<Link to="/home" className="font-semibold tracking-tight">
						d12-starter
					</Link>
				</div>
				{/* Flush flex column: each page owns its <PageHeader> (full-bleed hairline)
				    and a scrolling <PageBody>. See src/components/ui/page-header.tsx. */}
				<main className="flex min-w-0 flex-1 flex-col overflow-hidden">
					<Outlet />
				</main>
			</div>

			<CommandPalette open={cmdkOpen} onOpenChange={setCmdkOpen} items={commandItems} />
		</div>
	);
}
